import { type AccountInfo, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { Global } from "@emotion/react";
import { MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createHashHistory, createRouter, RouterProvider } from "@tanstack/react-router";
import { FlagsProvider } from "flagged";
import "mantine-react-table/styles.css";
import React from "react";
import { createRoot } from "react-dom/client";
import { ApiHealthProvider } from "./contexts/apiHealthContext";
import { TabProvider } from "./contexts/tabContext";
import { TourProvider } from "./contexts/tourContext";
import { routeTree } from "./routeTree.gen";
import "./styles/style.scss";
import { AuthenticationWrapper } from "./components/AuthenticationWrapper";
import { SE_THEME } from "./styles/se-energy.theme";
import { loginRequest } from "./utils/authConfig";
import { corruptToken, simulateAuthError } from "./utils/authDebugger";
import { createMsalInstance } from "./utils/useMsalInstance";

// Define interface for window with our custom property
interface CustomWindow extends Window {
    simulateAuthError?: typeof simulateAuthError;
    corruptToken?: typeof corruptToken;
}

if (import.meta.env.VITE_ENVIRONMENT === "dev") {
    (window as CustomWindow).simulateAuthError = simulateAuthError;
    (window as CustomWindow).corruptToken = corruptToken;
}

const hashHistory = createHashHistory();

// Create a new router instance
// @ts-expect-error TODO This fails with "strictNullChecks must be enabled in tsconfig.json",
// but those are currently intentionally disabled because they cause additional TS errors
// which will be fixed separately.
export const router = createRouter({
    history: hashHistory,
    routeTree,
    context: {
        queryClient: new QueryClient(),
    },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

const domElementId = "app";

// Render the app
const rootElement = document.getElementById(domElementId);
if (!rootElement) {
    throw new Error(`Element with id ${domElementId} not found`);
}

const environment = import.meta.env.VITE_ENVIRONMENT;
const isPlaywright = window.location.search.includes("isPlaywright=true");

// Detect copilot/widget mode early for Sentry config
const isCopilotModeEarly =
    window.location.hash.includes("/copilot") || window.location.search.includes("mode=copilot");
const isWidgetModeEarly =
    window.location.hash.includes("/widget/") || window.location.search.includes("mode=widget");

if (environment && environment !== "local" && !isPlaywright) {
    Sentry.init({
        dsn: "https://8f33466b4db54a81a4676c90d9d3da48@crash.siemens-energy.com/67",
        beforeSend(event, hint) {
            // Skip Sentry entirely in Playwright tests (flag is set via addInitScript)
            if ((window as unknown as { __IS_PLAYWRIGHT__?: boolean }).__IS_PLAYWRIGHT__) {
                return null;
            }

            // Filter out BrowserAuthError - expected during auth flows (token expired, auth in progress)
            const error = hint?.originalException;
            if (error instanceof Error) {
                // Ignore MSAL BrowserAuthError - these are expected during auth flows
                if (
                    error.name === "BrowserAuthError" ||
                    error.message?.includes("BrowserAuthError")
                ) {
                    return null;
                }
            }

            // Check if it is an exception, and if so, show the report dialog
            if (event.exception && event.event_id) {
                Sentry.showReportDialog({ eventId: event.event_id });
            }

            return event;
        },

        integrations: [
            // See docs for support of different versions of variation of react router
            // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
            Sentry.tanstackRouterBrowserTracingIntegration(router),
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
            // Capture console.error() calls as Sentry errors
            Sentry.captureConsoleIntegration({ levels: ["error"] }),
            ...(import.meta.env.VITE_FEATURES?.includes("SENTRY_USER_FEEDBACK")
                ? [
                      Sentry.feedbackIntegration({
                          // Disable auto-inject in copilot/widget mode - we'll use our own button
                          autoInject: !isCopilotModeEarly && !isWidgetModeEarly,
                          // Show both name and email fields (pre-filled from MSAL)
                          showName: true,
                          showEmail: true,
                          // Enable screenshot capture
                          enableScreenshot: true,
                          // Form customization
                          formTitle: "Report a Bug",
                          submitButtonLabel: "Send Report",
                          successMessageText: "Thank you! Your feedback has been sent.",
                          // Helpful message for network issues (ZScaler blocking)
                          messagePlaceholder:
                              "Describe what happened...\n\nIf you have network issues sending this report, please email: CTSKS.Support@siemens-energy.com",
                          // Position button higher to avoid overlapping text area
                          buttonLabel: "Report Bug",
                          triggerLabel: "Report Bug",
                          themeLight: {
                              submitBackground: "#4D217A",
                              submitBackgroundHover: "#5D318A",
                          },
                          themeDark: {
                              submitBackground: "#4D217A",
                              submitBackgroundHover: "#5D318A",
                          },
                      }),
                  ]
                : []),
        ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,

        // Session Replay sample rates
        replaysSessionSampleRate: 0.1, // Capture 10% of all sessions
        replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors

        environment: environment,

        // Ignore errors from browser extensions and expected auth errors
        ignoreErrors: [
            // Browser extension / ad blocker errors
            "ERR_BLOCKED_BY_CLIENT",
            "Failed to fetch",
            "NetworkError",
            // Promise rejections without proper Error objects
            "Non-Error promise rejection captured",
            // MSAL auth errors - expected during login/token refresh flows
            /^BrowserAuthError/,
            "BrowserAuthError",
            "interaction_in_progress",
            // MSAL silent auth fallback - expected when tokens need refresh
            /useMsalAuthentication.*Interaction required/,
            // User-initiated cancellations
            "cancelled",
        ],
    });
}

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = createMsalInstance();

const queryClient = new QueryClient();

// Detect if running in widget/iframe mode - widget handles its own auth via WidgetAuthProvider
const isWidgetMode =
    window.location.hash.includes("/widget/") || window.location.search.includes("mode=widget");
const isCopilotMode =
    window.location.hash.includes("/copilot") || window.location.search.includes("mode=copilot");
const isInIframe = window.self !== window.top;

// Add body classes for copilot/widget mode (used by CSS to hide Sentry widget)
if (isCopilotMode) {
    document.body.classList.add("copilot-mode");
}
if (isWidgetMode) {
    document.body.classList.add("widget-mode");
}

// Initialize MSAL and render app
(async () => {
    await msalInstance.initialize();

    // Skip redirect handling when in widget/iframe/copilot mode
    // Widget and copilot use popup auth (not redirect)
    if (!isWidgetMode && !isCopilotMode && !isInIframe) {
        // Handle the redirect promise before rendering
        await msalInstance.handleRedirectPromise().catch((error) => {
            console.error("Redirect promise failed: ", error);
        });
    }

    // Default to using the first account if no account is active on page load
    // Helper to set Sentry user with both email and name for bug report pre-filling
    const setSentryUser = (account: AccountInfo | null) => {
        if (account) {
            Sentry.setUser({
                email: account.username,
                username: account.name || account.username,
            });
        }
    };

    if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
        // Account selection logic is app dependent. Adjust as needed for different use cases.
        msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
        setSentryUser(msalInstance.getActiveAccount());
    } else if (msalInstance?.getActiveAccount()) {
        setSentryUser(msalInstance.getActiveAccount());
    }

    msalInstance.addEventCallback((event) => {
        if (
            event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            event.eventType === EventType.SSO_SILENT_SUCCESS
        ) {
            const payload = event.payload as { account?: AccountInfo };
            if (payload?.account) {
                msalInstance.setActiveAccount(payload.account);
                setSentryUser(payload.account);
            }
        }
        if (event.eventType === EventType.LOGIN_SUCCESS) {
            localStorage.setItem("lastLoginTime", event.timestamp.toString());
        }
    });

    createRoot(rootElement).render(
        <React.StrictMode>
            <MsalProvider instance={msalInstance}>
                <MantineProvider theme={SE_THEME} defaultColorScheme="light">
                    <Global
                        styles={() => ({
                            body: {
                                backgroundColor:
                                    SE_THEME.colorScheme === "dark"
                                        ? SE_THEME.colors.dark[9]
                                        : SE_THEME.colors.gray[0],
                                margin: 0,
                                padding: 0,
                                fontFamily: SE_THEME.fontFamily,
                            },
                        })}
                    />
                    <AuthenticationWrapper loginRequest={loginRequest()}>
                        <ApiHealthProvider>
                            <QueryClientProvider client={queryClient}>
                                <FlagsProvider
                                    features={
                                        import.meta.env.VITE_FEATURES
                                            ? [...import.meta.env.VITE_FEATURES.split(",")]
                                            : []
                                    }
                                >
                                    <TabProvider>
                                        <TourProvider tourId="main-tour">
                                            <RouterProvider router={router} />
                                        </TourProvider>
                                    </TabProvider>
                                </FlagsProvider>
                            </QueryClientProvider>
                        </ApiHealthProvider>
                    </AuthenticationWrapper>
                </MantineProvider>
            </MsalProvider>
        </React.StrictMode>,
    );
})();
